import * as THREE from 'three';
import type { Obstacle } from '../game/Pilot';
import {
  GATE_D,
  GATE_HALF_GAP,
  HERONS,
  LOGS_END,
  LOGS_START,
  TUNNEL_END,
  type FlowSample,
  type River,
} from '../game/River';
import { DUCK_RADIUS } from '../game/tuning';
import { disposeObject3D } from '../utils/dispose';
import type { Duck } from './Duck';

const LOG_COUNT = 5;
const LOG_RADIUS = 0.22;
/** Logs are heavy: they drift a little slower than the water around them. */
const LOG_DRIFT = 0.8;

/** Sluice timing: the gate is open for GATE_OPEN seconds of every GATE_PERIOD. */
const GATE_PERIOD = 3.8;
const GATE_OPEN = 2;
const GATE_WALL_HALF_THICK = 0.25;

/** Furthest the beak can land from the heron's feet (it lunges for the far end). */
const HERON_REACH = 2.6;
/** How far the beak reaches with a neck strike alone, before the body lunges. */
const HERON_NECK_REACH = 1.5;
const HERON_WINDUP = 0.8;
const HERON_STRIKE = 0.16;
const HERON_RECOVER = 0.9;
const HERON_HIT_RADIUS = 0.85;
/** Herons only hunt when the duck is this close upstream (m), and in view. */
const HERON_WAKE_RANGE = 7;

export type HazardEvents = {
  /** 'gate' when the duck bumps the closed sluice, 'snap' when a heron gets it. */
  hit: 'gate' | 'snap' | null;
  /** A heron just started winding up (for a warning sound). */
  heronWindup: boolean;
  /** The sluice gate just started opening or closing. */
  gateMoved: 'open' | 'close' | null;
};

type Log = {
  x: number;
  z: number;
  angle: number;
  spin: number;
  halfLength: number;
  /** False once it has floated out of its stretch (hidden, no collisions). */
  active: boolean;
  vx: number;
  vz: number;
  mesh: THREE.Group;
};

type HeronState = 'idle' | 'windup' | 'strike' | 'recover';

type Heron = {
  d: number;
  side: number;
  baseX: number;
  baseY: number;
  /** Unit direction and distance of the body lunge for the current strike. */
  lungeX: number;
  lungeZ: number;
  lunge: number;
  state: HeronState;
  timer: number;
  targetX: number;
  targetZ: number;
  group: THREE.Group;
  neck: THREE.Group;
  ring: THREE.Mesh;
};

/**
 * The moving set pieces: drifting logs, the sluice gate and the herons.
 * Logic is deterministic from race time and the seeded RNG so the headless
 * sim and ghosts stay honest.
 */
export class Hazards {
  readonly group = new THREE.Group();
  readonly events: HazardEvents = { hit: null, heronWindup: false, gateMoved: null };
  private readonly logs: Log[] = [];
  private readonly herons: Heron[] = [];
  private readonly door: THREE.Mesh;
  private readonly lamp: THREE.Mesh;
  private readonly lampMaterial: THREE.MeshStandardMaterial;
  private readonly sample: FlowSample = { x: 0, z: 0, speed: 0, whirl: 0 };
  private gateOpen = true;
  private doorLift = 1;
  private readonly gapX: number;
  private readonly waterAtGate: number;

  constructor(
    private readonly river: River,
    private readonly rng: () => number,
  ) {
    const logMaterial = new THREE.MeshStandardMaterial({ color: '#7a5433', roughness: 0.95, flatShading: true });
    const endMaterial = new THREE.MeshStandardMaterial({ color: '#d9b27c', roughness: 0.9 });
    for (let i = 0; i < LOG_COUNT; i += 1) {
      const halfLength = 1 + this.rng() * 0.35;
      const mesh = new THREE.Group();
      const body = new THREE.Mesh(new THREE.CylinderGeometry(LOG_RADIUS, LOG_RADIUS, halfLength * 2, 9, 1, true), logMaterial);
      body.rotation.z = Math.PI / 2;
      mesh.add(body);
      for (const end of [-1, 1]) {
        const cap = new THREE.Mesh(new THREE.CircleGeometry(LOG_RADIUS, 9), endMaterial);
        cap.rotation.y = (end * Math.PI) / 2;
        cap.position.x = end * halfLength;
        mesh.add(cap);
      }
      const knot = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.07, 0.35, 5), logMaterial);
      knot.position.set(halfLength * 0.3, 0.2, 0);
      knot.rotation.z = -0.6;
      mesh.add(knot);
      mesh.traverse((o) => {
        if ((o as THREE.Mesh).isMesh) o.castShadow = true;
      });
      this.group.add(mesh);
      this.logs.push({ x: 0, z: 0, angle: 0, spin: 0, halfLength, active: true, vx: 0, vz: 0, mesh });
    }

    // Sluice: stone walls from each bank to the gap, a wooden gate that lifts, a lamp.
    this.gapX = river.laneX(GATE_D);
    this.waterAtGate = river.heightAt(GATE_D);
    const stone = new THREE.MeshStandardMaterial({ color: '#9a978c', roughness: 0.9, flatShading: true });
    const left = river.xAt(GATE_D, -1) - 1.2;
    const right = river.xAt(GATE_D, 1) + 1.2;
    for (const [from, to] of [
      [left, this.gapX - GATE_HALF_GAP],
      [this.gapX + GATE_HALF_GAP, right],
    ]) {
      const wall = new THREE.Mesh(new THREE.BoxGeometry(to - from, 1.8, GATE_WALL_HALF_THICK * 2), stone);
      wall.position.set((from + to) / 2, this.waterAtGate + 0.4, -GATE_D);
      wall.castShadow = true;
      wall.receiveShadow = true;
      this.group.add(wall);
    }
    const beam = new THREE.Mesh(new THREE.BoxGeometry(GATE_HALF_GAP * 2 + 0.6, 0.35, 0.6), stone);
    beam.position.set(this.gapX, this.waterAtGate + 2.5, -GATE_D);
    beam.castShadow = true;
    this.group.add(beam);
    for (const side of [-1, 1]) {
      const pillar = new THREE.Mesh(new THREE.BoxGeometry(0.3, 2.8, 0.6), stone);
      pillar.position.set(this.gapX + side * (GATE_HALF_GAP + 0.15), this.waterAtGate + 1.1, -GATE_D);
      pillar.castShadow = true;
      this.group.add(pillar);
    }
    this.door = new THREE.Mesh(
      new THREE.BoxGeometry(GATE_HALF_GAP * 2, 1.3, 0.18),
      new THREE.MeshStandardMaterial({ color: '#b04a2e', roughness: 0.8 }),
    );
    this.door.castShadow = true;
    this.group.add(this.door);
    this.lampMaterial = new THREE.MeshStandardMaterial({ color: '#1d1d1d', emissive: '#35d05a', emissiveIntensity: 1.6 });
    this.lamp = new THREE.Mesh(new THREE.SphereGeometry(0.22, 12, 10), this.lampMaterial);
    // On top of the right pillar, out of the camera's line to the duck.
    this.lamp.position.set(this.gapX + GATE_HALF_GAP + 0.15, this.waterAtGate + 2.75, -GATE_D);
    this.group.add(this.lamp);

    for (const [d, side] of HERONS) this.herons.push(this.buildHeron(d, side));
  }

  private buildHeron(d: number, side: number): Heron {
    const grey = new THREE.MeshStandardMaterial({ color: '#8e9aa6', roughness: 0.7 });
    const dark = new THREE.MeshStandardMaterial({ color: '#4a525c', roughness: 0.7 });
    const beakMaterial = new THREE.MeshStandardMaterial({ color: '#e0b43a', roughness: 0.5 });
    const group = new THREE.Group();
    // Wading in the shallows by the bank, legs in the water.
    const baseX = this.river.xAt(d, side * 0.8);
    const baseY = this.river.heightAt(d, baseX) - 0.3;
    group.position.set(baseX, baseY, -d);

    for (const leg of [-0.1, 0.1]) {
      const l = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 0.9, 5), dark);
      l.position.set(leg, 0.45, 0);
      group.add(l);
    }
    const body = new THREE.Mesh(new THREE.SphereGeometry(0.32, 14, 10), grey);
    body.scale.set(0.8, 0.85, 1.4);
    body.position.y = 1.05;
    group.add(body);
    const tail = new THREE.Mesh(new THREE.ConeGeometry(0.18, 0.5, 8), dark);
    tail.rotation.x = Math.PI / 2 + 0.4;
    tail.position.set(0, 1.0, 0.45);
    group.add(tail);

    // The neck pivots at the shoulder; the head and beak sit at its tip.
    const neck = new THREE.Group();
    neck.position.set(0, 1.25, -0.25);
    const neckMesh = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.07, 0.8, 7), grey);
    neckMesh.position.y = 0.4;
    neck.add(neckMesh);
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.11, 10, 8), grey);
    head.position.y = 0.82;
    neck.add(head);
    const beak = new THREE.Mesh(new THREE.ConeGeometry(0.04, 0.42, 7), beakMaterial);
    beak.position.set(0, 0.82, -0.26);
    beak.rotation.x = -Math.PI / 2;
    neck.add(beak);
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.02, 6, 5), new THREE.MeshBasicMaterial({ color: '#111' }));
    eye.position.set(side * -0.08, 0.86, -0.06);
    neck.add(eye);
    group.add(neck);
    group.traverse((o) => {
      if ((o as THREE.Mesh).isMesh) o.castShadow = true;
    });
    group.rotation.set(0, side > 0 ? Math.PI / 2 : -Math.PI / 2, 0, 'YXZ');
    this.group.add(group);

    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.6, 0.85, 28),
      new THREE.MeshBasicMaterial({ color: '#ff3b30', transparent: true, opacity: 0.85, depthWrite: false }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.renderOrder = 3;
    ring.visible = false;
    this.group.add(ring);

    return {
      d,
      side,
      baseX,
      baseY,
      lungeX: 0,
      lungeZ: 0,
      lunge: 0,
      state: 'idle',
      timer: 1,
      targetX: 0,
      targetZ: 0,
      group,
      neck,
      ring,
    };
  }

  reset(): void {
    this.logs.forEach((log, i) => {
      const d = LOGS_START + 3 + (i + this.rng() * 0.6) * ((LOGS_END - LOGS_START - 6) / LOG_COUNT);
      this.placeLog(log, d);
    });
    for (const heron of this.herons) {
      heron.state = 'idle';
      heron.timer = 0.6;
      heron.ring.visible = false;
      heron.neck.rotation.set(0, 0, 0);
    }
    this.gateOpen = true;
    this.doorLift = 1;
    this.syncDoor();
  }

  private placeLog(log: Log, d: number): void {
    log.x = this.river.xAt(d, (this.rng() * 2 - 1) * 0.6);
    log.z = -d;
    log.angle = this.rng() * Math.PI;
    log.spin = (this.rng() * 2 - 1) * 0.5;
    log.active = true;
    log.mesh.visible = true;
  }

  /** Points along each floating log, for AI ducks to steer around. */
  logObstacles(): Obstacle[] {
    const out: Obstacle[] = [];
    for (const log of this.logs) {
      if (!log.active) continue;
      const ax = Math.cos(log.angle) * log.halfLength;
      const az = -Math.sin(log.angle) * log.halfLength;
      for (const t of [-0.7, 0, 0.7]) out.push({ x: log.x + ax * t, d: -(log.z + az * t), r: LOG_RADIUS + 0.25 });
    }
    return out;
  }

  gateIsOpen(): boolean {
    return this.gateOpen;
  }

  /**
   * Advance hazards and resolve their contacts with every duck. ducks[0] is
   * the player: only its hits are reported in `events`.
   */
  update(delta: number, raceTime: number, ducks: Duck[], racing: boolean): void {
    this.events.hit = null;
    this.events.heronWindup = false;
    this.events.gateMoved = null;
    this.updateLogs(delta, ducks);
    this.updateGate(delta, raceTime, ducks);
    this.updateHerons(delta, ducks, racing);
  }

  private updateLogs(delta: number, ducks: Duck[]): void {
    const player = ducks[0];
    for (const log of this.logs) {
      if (!log.active) continue;
      this.river.flowAt(log.x, log.z, this.sample);
      log.vx = this.sample.x * LOG_DRIFT;
      log.vz = this.sample.z * LOG_DRIFT;
      log.x += log.vx * delta;
      log.z += log.vz * delta;
      log.angle += log.spin * delta;
      const d = -log.z;
      // Keep logs inside the banks.
      const cx = this.river.centerX(d);
      const limit = this.river.halfWidth(d) - log.halfLength * 0.6;
      log.x = Math.max(cx - limit, Math.min(cx + limit, log.x));
      // Past the stretch: recycle to the top while the duck is still upstream, else sink out of sight.
      if (d > LOGS_END + 4) {
        if (player.distance < LOGS_START - 6) {
          this.placeLog(log, LOGS_START + this.rng() * 3);
        } else {
          log.active = false;
          log.mesh.visible = false;
          continue;
        }
      }

      const y = this.river.heightAt(d, log.x) + 0.05;
      log.mesh.position.set(log.x, y, log.z);
      log.mesh.rotation.set(0, log.angle, 0);

      // Capsule vs each duck.
      const ax = Math.cos(log.angle) * log.halfLength;
      const az = -Math.sin(log.angle) * log.halfLength;
      for (const duck of ducks) {
        if (duck.heightAboveWater > 0.45) continue;
        const px = duck.position.x - log.x;
        const pz = duck.position.z - log.z;
        const t = Math.max(-1, Math.min(1, (px * ax + pz * az) / (log.halfLength * log.halfLength)));
        const cxp = log.x + ax * t;
        const czp = log.z + az * t;
        const dx = duck.position.x - cxp;
        const dz = duck.position.z - czp;
        const dist = Math.hypot(dx, dz);
        const minDist = LOG_RADIUS + DUCK_RADIUS;
        if (dist < minDist) {
          const nx = dist > 0.001 ? dx / dist : 1;
          const nz = dist > 0.001 ? dz / dist : 0;
          duck.contact(nx, nz, cxp + nx * minDist, czp + nz * minDist, log.vx, log.vz);
        }
      }
    }
  }

  private updateGate(delta: number, raceTime: number, ducks: Duck[]): void {
    const open = raceTime % GATE_PERIOD < GATE_OPEN;
    if (open !== this.gateOpen) this.events.gateMoved = open ? 'open' : 'close';
    this.gateOpen = open;
    this.doorLift += ((open ? 1 : 0) - this.doorLift) * (1 - Math.exp(-10 * delta));
    this.syncDoor();

    // The wall is a line across the river; the gap only lets you through when open.
    const front = GATE_D - GATE_WALL_HALF_THICK - DUCK_RADIUS;
    ducks.forEach((duck, i) => {
      const d = duck.distance;
      if (d <= front || d >= GATE_D + 0.1 || duck.heightAboveWater >= 1) return;
      const inGap = Math.abs(duck.position.x - this.gapX) < GATE_HALF_GAP - DUCK_RADIUS * 0.6;
      if (inGap && this.doorLift >= 0.6) return;
      const before = duck.events.rockHit;
      // Measured against the water's own motion: drifting onto the wall is a
      // gentle rest, only paddling into it is an impact.
      duck.contact(0, 1, duck.position.x, -front, duck.flow.x, duck.flow.z);
      if (i === 0 && duck.events.rockHit > before) this.events.hit = 'gate';
    });
  }

  private syncDoor(): void {
    this.door.position.set(this.gapX, this.waterAtGate + 0.35 + this.doorLift * 1.6, -GATE_D);
    this.lampMaterial.emissive.set(this.gateOpen ? '#35d05a' : '#ff3b30');
  }

  private updateHerons(delta: number, ducks: Duck[], racing: boolean): void {
    for (const heron of this.herons) {
      // Hunt the nearest duck that's in its window.
      let duck: Duck | null = null;
      let best = Infinity;
      for (const candidate of ducks) {
        const ahead = heron.d - candidate.distance;
        if (ahead >= HERON_WAKE_RANGE || ahead <= -3 || candidate.distance <= TUNNEL_END + 1) continue;
        const dist = Math.hypot(candidate.position.x - heron.baseX, candidate.position.z - -heron.d);
        if (dist < best) {
          best = dist;
          duck = candidate;
        }
      }
      const awake = racing && duck !== null;
      heron.timer -= delta;
      switch (heron.state) {
        case 'idle':
          heron.neck.rotation.x = Math.sin(heron.timer * 3) * 0.05;
          if (awake && duck && heron.timer <= 0) {
            // Aim where the duck is heading, within reach of the bank.
            const lead = 0.75;
            let tx = duck.position.x + duck.velocity.x * lead;
            let tz = duck.position.z + duck.velocity.z * lead;
            const dx = tx - heron.baseX;
            const dz = tz - -heron.d;
            const dist = Math.hypot(dx, dz);
            if (dist > HERON_REACH) {
              tx = heron.baseX + (dx / dist) * HERON_REACH;
              tz = -heron.d + (dz / dist) * HERON_REACH;
            }
            heron.targetX = tx;
            heron.targetZ = tz;
            const reach = Math.hypot(tx - heron.baseX, tz - -heron.d);
            heron.lunge = Math.max(0, reach - HERON_NECK_REACH);
            heron.lungeX = reach > 0.01 ? (tx - heron.baseX) / reach : 0;
            heron.lungeZ = reach > 0.01 ? (tz - -heron.d) / reach : 0;
            heron.state = 'windup';
            heron.timer = HERON_WINDUP;
            heron.ring.visible = true;
            if (duck === ducks[0]) this.events.heronWindup = true;
          }
          break;
        case 'windup': {
          // Neck cocks back; the ring on the water pulses faster as it's about to strike.
          const k = 1 - heron.timer / HERON_WINDUP;
          heron.neck.rotation.x = 0.5 * k;
          this.poseHeron(heron, -0.15 * k, 0.15 * k);
          const pulse = 1 + Math.sin(k * k * 30) * 0.12;
          heron.ring.scale.setScalar(pulse);
          if (heron.timer <= 0) {
            heron.state = 'strike';
            heron.timer = HERON_STRIKE;
          }
          break;
        }
        case 'strike': {
          // Lunge: body steps in and tips forward, neck snaps down to the ring.
          const k = 1 - Math.max(0, heron.timer) / HERON_STRIKE;
          heron.neck.rotation.x = 0.5 - 2.3 * k;
          this.poseHeron(heron, k, -0.6 * k);
          if (heron.timer <= 0) {
            // Any duck caught in the ring gets snapped.
            ducks.forEach((victim, i) => {
              const hit =
                Math.hypot(victim.position.x - heron.targetX, victim.position.z - heron.targetZ) < HERON_HIT_RADIUS &&
                victim.heightAboveWater < 0.4;
              if (!hit) return;
              const nx = victim.position.x - heron.baseX;
              const nz = victim.position.z - -heron.d;
              const len = Math.hypot(nx, nz) || 1;
              victim.spinOut(nx / len, nz / len);
              victim.velocity.x += (nx / len) * 2.5;
              victim.velocity.z += (nz / len) * 2.5;
              if (i === 0) this.events.hit = 'snap';
            });
            heron.state = 'recover';
            heron.timer = HERON_RECOVER;
            heron.ring.visible = false;
          }
          break;
        }
        case 'recover':
          const k = heron.timer / HERON_RECOVER;
          heron.neck.rotation.x = -1.8 * k;
          this.poseHeron(heron, k, -0.6 * k);
          if (heron.timer <= 0) {
            heron.state = 'idle';
            heron.timer = 0.35;
          }
          break;
      }
      if (heron.ring.visible) {
        heron.ring.position.set(
          heron.targetX,
          this.river.heightAt(-heron.targetZ, heron.targetX) + 0.06,
          heron.targetZ,
        );
      }
      if (heron.state === 'idle') this.poseHeron(heron, 0, 0);
    }
  }

  /**
   * Place the heron: `lunge` 0..1 of the way along its strike lunge (negative
   * leans back), `pitch` tips the body forward (negative) toward the target.
   */
  private poseHeron(heron: Heron, lunge: number, pitch: number): void {
    const step = heron.lunge * lunge;
    heron.group.position.set(heron.baseX + heron.lungeX * step, heron.baseY, -heron.d + heron.lungeZ * step);
    // Face the target while hunting; face across the river when idle.
    const yaw =
      heron.state === 'idle'
        ? heron.side > 0
          ? Math.PI / 2
          : -Math.PI / 2
        : Math.atan2(-heron.lungeX, -heron.lungeZ);
    heron.group.rotation.set(pitch, yaw, 0, 'YXZ');
  }

  dispose(): void {
    disposeObject3D(this.group);
  }
}
